import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── DB settings ───────────────────────────────────────────────────────────────

export function useAllSettings() {
  return useQuery<Record<string, string>>({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string>>('/api/settings'),
  });
}

export function useUpdateSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<{ ok: boolean }>(`/api/settings/key/${key}`, { value }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

// ── Keytar secrets ────────────────────────────────────────────────────────────

export function useSecretStatus(key: string) {
  return useQuery<{ key: string; configured: boolean }>({
    queryKey: ['secret-status', key],
    queryFn: () => api.get<{ key: string; configured: boolean }>(`/api/settings/secrets/${key}`),
  });
}

export function useUpdateSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<{ ok: boolean }>(`/api/settings/secrets/${key}`, { value }),
    onSuccess: (_d, vars) => void qc.invalidateQueries({ queryKey: ['secret-status', vars.key] }),
  });
}

// ── LLM spend ─────────────────────────────────────────────────────────────────

export interface LlmSpend {
  todaySpend: number;
  cap: number;
  remaining: number;
  date: string;
}

export function useLlmSpend() {
  return useQuery<LlmSpend>({
    queryKey: ['llm-spend'],
    queryFn: () => api.get<LlmSpend>('/api/llm/spend'),
    refetchInterval: 60_000,
  });
}

// ── IMAP accounts ─────────────────────────────────────────────────────────────

export function useEmailAccounts() {
  return useQuery<string[]>({
    queryKey: ['email-accounts'],
    queryFn: () => api.get<string[]>('/api/email/accounts'),
  });
}

export function useAddEmailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; host: string; port?: string; secure?: string; user: string; pass: string }) =>
      api.post<{ ok: boolean; name: string }>('/api/email/accounts', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-accounts'] }),
  });
}

export function useTestEmailAccount() {
  return useMutation({
    mutationFn: (name: string) =>
      api.post<{ ok: boolean; error?: string }>(`/api/email/accounts/${name}/test`, {}),
  });
}

// ── Account signature ─────────────────────────────────────────────────────────

export function useAccountSignature(name: string | undefined) {
  return useQuery<{ signature: string | null }>({
    queryKey: ['account-signature', name],
    queryFn: () => api.get<{ signature: string | null }>(`/api/email/accounts/${encodeURIComponent(name!)}/signature`),
    enabled: !!name,
    staleTime: 30_000,
  });
}

export function useUpdateAccountSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, signature }: { name: string; signature: string }) =>
      api.put<{ ok: boolean }>(`/api/email/accounts/${encodeURIComponent(name)}/signature`, { signature }),
    onSuccess: (_d, vars) => void qc.invalidateQueries({ queryKey: ['account-signature', vars.name] }),
  });
}

// ── Audit log ─────────────────────────────────────────────────────────────────

export interface AuditRow {
  id: number;
  action: string;
  entityType: string | null;
  entityId: string | null;
  paramsJson: string | null;
  createdAt: string;
}

export interface AuditResult {
  rows: AuditRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export function useAuditLog(page = 1, limit = 50) {
  return useQuery<AuditResult>({
    queryKey: ['audit-log', page, limit],
    queryFn: () =>
      api.get<AuditResult>(`/api/settings/audit-log?page=${page}&limit=${limit}`),
  });
}

// ── Lead Engine settings ───────────────────────────────────────────────────────

export interface LeadEngineSettings {
  followupCount: number;
  followupDelay1: number;
  followupDelay2: number;
  followupDelay3: number;
  signatureHtml: string;
  fromAccount: string;
  dailyCap: number;
  autoSendStartHour: number;
  autoSendEndHour: number;
  autoCloseGraceDays: number;
}

const DEFAULT_LEAD_ENGINE: LeadEngineSettings = {
  followupCount: 2,
  followupDelay1: 3,
  followupDelay2: 7,
  followupDelay3: 14,
  signatureHtml: '',
  fromAccount: '',
  dailyCap: 10,
  autoSendStartHour: 9,
  autoSendEndHour: 17,
  autoCloseGraceDays: 2,
};

export function useLeadEngineSettings() {
  return useQuery<LeadEngineSettings>({
    queryKey: ['lead-engine-settings'],
    queryFn: () => api.get<LeadEngineSettings>('/api/settings/lead-engine'),
    placeholderData: DEFAULT_LEAD_ENGINE,
  });
}

export function useUpdateLeadEngineSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<LeadEngineSettings>) =>
      api.patch<{ ok: boolean }>('/api/settings/lead-engine', patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-engine-settings'] }),
  });
}

// ── Lead pipelines ────────────────────────────────────────────────────────────

export type ScrapeZone = { city: string; state: string };

export interface LeadPipeline {
  id: number;
  name: string;
  enabled: number;
  categories: string[];
  zones: ScrapeZone[];
  capPerRun: number;
  cronHour: number;
  cronMinute: number;
  intent: string;
  autoScrape: number;
  autoEnrich: number;
  autoAnalysis: number;
  autoDraft: number;
  autoSend: number;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunCount: number | null;
  exhausted: boolean;
}

export interface LeadPipelineInput {
  name: string;
  enabled: boolean;
  categories: string[];
  zones: ScrapeZone[];
  capPerRun: number;
  cronHour: number;
  cronMinute: number;
  intent: string;
  autoScrape: boolean;
  autoEnrich: boolean;
  autoAnalysis: boolean;
  autoDraft: boolean;
  autoSend: boolean;
}

export function useLeadPipelines() {
  return useQuery<LeadPipeline[]>({
    queryKey: ['lead-pipelines'],
    queryFn: () => api.get<LeadPipeline[]>('/api/lead-pipelines'),
    placeholderData: [],
    refetchInterval: (query) => {
      const pipelines = query.state.data;
      return Array.isArray(pipelines) &&
        pipelines.some(p => p.lastRunStatus === 'running' || p.lastRunStatus === 'queued')
        ? 5_000
        : false;
    },
  });
}

export function useCreateLeadPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeadPipelineInput) =>
      api.post<{ ok: boolean; id: number }>('/api/lead-pipelines', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

export function useUpdateLeadPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & LeadPipelineInput) =>
      api.put<{ ok: boolean }>(`/api/lead-pipelines/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

export function useDeleteLeadPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ ok: boolean }>(`/api/lead-pipelines/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

export function useRunPipelineNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ ok: boolean; runsQueued: number }>(`/api/lead-pipelines/${id}/run-now`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

// ── OpenClaw (Settings → OpenClaw) ───────────────────────────────────────────

export interface OpenClawStatus {
  consumer: string;
  endpoint: string;
  tokenIssued: boolean;
  lastSeenAt: string | null;
}

export function useOpenClawStatus() {
  return useQuery<OpenClawStatus>({
    queryKey: ['openclaw-status'],
    queryFn: () => api.get('/api/settings/openclaw'),
    staleTime: 10_000,
  });
}

export function useIssueOpenClawToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ token: string }>('/api/settings/openclaw/issue-token', {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['openclaw-status'] }),
  });
}

// ── Users (Fase 2 — Settings → Usuarios) ─────────────────────────────────────

export interface AppUser {
  id: number;
  email: string;
  displayName: string | null;
  role: 'owner' | 'member';
  active: boolean;
  createdAt: string;
}

export function useUsers() {
  return useQuery<{ users: AppUser[] }>({
    queryKey: ['users'],
    queryFn: () => api.get('/api/settings/users'),
    staleTime: 10_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; password: string; displayName?: string | undefined; role: 'owner' | 'member' }) =>
      api.post<AppUser>('/api/settings/users', data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: 'owner' | 'member' }) =>
      api.patch<AppUser>(`/api/settings/users/${id}/role`, { role }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.patch<AppUser>(`/api/settings/users/${id}/active`, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ── API usage (Fase 4 — Settings → Consumo) ──────────────────────────────────

export interface ApiUsageServiceBreakdown {
  service: string;
  quantity: number;
  costUsd: number;
}

export interface ApiUsageSummary {
  month: string;
  monthSpendUsd: number;
  estimatedChargeUsd: number;
  byService: ApiUsageServiceBreakdown[];
  limitUsd: number | null;
  billingExempt: boolean;
}

export function useApiUsage() {
  return useQuery<ApiUsageSummary>({
    queryKey: ['api-usage'],
    queryFn: () => api.get('/api/settings/api-usage'),
    staleTime: 30_000,
  });
}

export function useSetApiUsageLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limitUsd: number | null) =>
      api.put<{ ok: boolean; limitUsd: number | null }>('/api/settings/api-usage/limit', { limitUsd }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['api-usage'] }),
  });
}

// ── Subscription (Fase 3 — Settings → Suscripción) ───────────────────────────

export interface SubscriptionSummary {
  billingExempt: boolean;
  status: 'active' | 'grace_period' | 'suspended';
  priceId: string | null;
  hasCustomer: boolean;
  gracePeriodStartedAt: string | null;
  gracePeriodEndsAt: string | null;
  nextChargeAt: string | null;
  nextChargeAmount: number | null;
  nextChargeCurrency: string | null;
  paymentMethod: { brand: string; last4: string } | null;
  invoices: Array<{ id: string; date: string | null; amountPaid: number; currency: string; status: string | null; hostedInvoiceUrl: string | null }>;
}

export function useSubscription() {
  return useQuery<SubscriptionSummary>({
    queryKey: ['subscription'],
    queryFn: () => api.get('/api/settings/subscription'),
    staleTime: 10_000,
  });
}

export function useSetSubscriptionPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (priceId: string) =>
      api.put<{ ok: boolean }>('/api/settings/subscription/price', { priceId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useCreateSubscriptionCheckout() {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/settings/subscription/checkout', {}),
  });
}

export function useCreateBillingPortal() {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/settings/subscription/portal', {}),
  });
}
